export function mappingKey (mapping) {
  const stopExternalId = mapping.stop_external_id || mapping.external_id || ''
  const legacyStopCode = stopExternalId ? '' : (mapping.legacy_stop_code || '')
  return [mapping.feed_key || '', stopExternalId, legacyStopCode].join('\u0000')
}

export function groupColorForKey (groupKey) {
  if (!groupKey) return null

  let hash = 2166136261
  for (let index = 0; index < groupKey.length; index += 1) {
    hash = Math.imul(hash ^ groupKey.charCodeAt(index), 16777619)
  }

  return `hsl(${(hash >>> 0) % 360} 72% 30%)`
}

export function selectedRowAfterResultsChange (selectedKey, rows) {
  if (!selectedKey) return null

  return rows.find((row) => mappingKey(row.mapping || row) === selectedKey) || null
}

export function selectionStateAfterDraftMutation (selectedKey, rows) {
  const selectedRow = selectedRowAfterResultsChange(selectedKey, rows)
  return selectedRow ? { selectedKey, selectedRow } : { selectedKey: null, selectedRow: null }
}

export function candidateRowWithDraftMapping (candidate, detail, mapping) {
  return {
    ...detail,
    ...candidate,
    stop_external_id: candidate.external_id,
    mapping,
    candidate: true,
    group_key: mapping?.group_key ?? null,
    group_name: mapping?.group_name ?? null,
    anchor: mapping?.anchor ?? false
  }
}

export function statusForStop (stop, mappings) {
  const mapping = mappings.find((candidate) => mappingKey(candidate) === mappingKey(stop))
  if (mapping) return mapping.group_key ? 'grouped' : 'standalone'
  if (stop.other_hubs?.length) return 'mapped-to-other-hub'
  return 'unmapped'
}

export function unresolvedRowsForView ({ readOnly, stopDetails, draft }) {
  const mappings = readOnly ? (stopDetails || []) : (draft?.mappings || [])
  return mappings.filter((mapping) => mapping.unresolved)
}

export function addCandidateToDraft (draft, candidate) {
  const row = {
    feed_key: candidate.feed_key,
    stop_external_id: candidate.external_id,
    legacy_stop_code: null,
    group_key: null,
    group_name: null,
    anchor: false
  }
  if (draft.mappings.some((mapping) => mappingKey(mapping) === mappingKey(row))) return draft

  return { ...draft, mappings: [...draft.mappings, row] }
}

export function groupKeyForSelection (mappings, selectedKeys, selectedGroupKey, newGroupKey) {
  if (selectedGroupKey) return selectedGroupKey

  const selected = new Set(selectedKeys)
  const existingGroup = mappings.find((mapping) => selected.has(mappingKey(mapping)) && mapping.group_key)
  return existingGroup?.group_key || newGroupKey
}

export function groupMappings (mappings, selectedKeys, { groupKey, groupName, anchorKey }) {
  const selected = new Set(selectedKeys)
  return mappings.map((mapping) => {
    if (!selected.has(mappingKey(mapping))) return mapping

    return {
      ...mapping,
      group_key: groupKey,
      group_name: groupName,
      anchor: mappingKey(mapping) === anchorKey
    }
  })
}

export function ungroupMappings (mappings, selectedKeys) {
  const selected = new Set(selectedKeys)
  return mappings.map((mapping) => selected.has(mappingKey(mapping))
    ? { ...mapping, group_key: null, group_name: null, anchor: false }
    : mapping)
}

export function refreshPreviewIfOpen (previewTarget, renderPreview) {
  if (previewTarget && !previewTarget.hidden) renderPreview()
}

export function readOnlyMapMarkers (rows) {
  const grouped = new Map()
  const standalone = []

  rows.forEach((row) => {
    if (row.group_key) {
      if (!grouped.has(row.group_key)) grouped.set(row.group_key, [])
      grouped.get(row.group_key).push(row)
    } else if (hasValidCoordinates(row)) {
      standalone.push({
        ...row,
        kind: 'standalone',
        name: row.name || row.code,
        latitude: Number(row.latitude),
        longitude: Number(row.longitude),
        fallback: false,
        representative: row,
        members: [row]
      })
    }
  })

  const groupMarkers = [...grouped.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([groupKey, members]) => {
      const sortedMembers = members.slice().sort(compareMembers)
      const validMembers = sortedMembers.filter(hasValidCoordinates)
      const anchor = validMembers.find((member) => member.anchor)
      const representative = anchor || validMembers[0]
      if (!representative) return null

      return {
        kind: 'group',
        group_key: groupKey,
        name: sortedMembers[0].group_name,
        latitude: Number(representative.latitude),
        longitude: Number(representative.longitude),
        fallback: !anchor,
        representative,
        members: sortedMembers
      }
    })
    .filter(Boolean)

  standalone.sort(compareMembers)
  return [...groupMarkers, ...standalone]
}

function compareMembers (left, right) {
  return left.feed_key.localeCompare(right.feed_key) ||
    (left.stop_external_id || '').localeCompare(right.stop_external_id || '')
}

function hasValidCoordinates (row) {
  if (row.longitude === null || row.longitude === undefined || row.longitude === '' ||
      row.latitude === null || row.latitude === undefined || row.latitude === '') return false

  const longitude = Number(row.longitude)
  const latitude = Number(row.latitude)
  return Number.isFinite(longitude) && longitude >= -180 && longitude <= 180 &&
    Number.isFinite(latitude) && latitude >= -90 && latitude <= 90
}

export function replaceDraftFromResponse (response) {
  return {
    revision: response.revision,
    feed_versions: { ...response.feed_versions },
    mappings: response.mappings.map((mapping) => ({ ...mapping }))
  }
}
